import { authHeaders } from './auth';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://nexa-backend-red.vercel.app';

export interface PortfolioItem {
  id: string;
  title: string;
  description: string;
  type: 'image' | 'video';
  media: string;
  category: string;
  websiteUrl: string;
  featured?: boolean;
}

export interface PortfolioResponse {
  data: PortfolioItem[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

export interface SinglePortfolioResponse {
  data: PortfolioItem;
}

export interface BulkUploadItem {
  id?: string;
  title: string;
  description: string;
  type: 'image' | 'video';
  media: string;
  category: string;
  websiteUrl: string;
  featured?: boolean;
}

export interface BulkUploadResponse {
  message: string;
  success: number;
  failed: number;
  results: Array<{
    id: string;
    originalId?: string;
    title: string;
    status: string;
  }>;
  errors: string[];
}

// Get all portfolio items
export const getPortfolio = async (params?: {
  category?: string;
  type?: 'image' | 'video';
  featured?: boolean;
  limit?: number;
  page?: number;
}): Promise<PortfolioResponse> => {
  try {
    const queryParams = new URLSearchParams();
    if (params?.category) queryParams.append('category', params.category);
    if (params?.type) queryParams.append('type', params.type);
    if (params?.featured !== undefined) queryParams.append('featured', params.featured.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.page) queryParams.append('page', params.page.toString());

    const url = `${API_BASE_URL}/api/portfolio${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch portfolio: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching portfolio:', error);
    throw error;
  }
};

// Get single portfolio item
export const getPortfolioItem = async (id: string): Promise<SinglePortfolioResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/portfolio/${id}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch portfolio item: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching portfolio item:', error);
    throw error;
  }
};

// Upload portfolio item
export const uploadPortfolio = async (formData: FormData): Promise<SinglePortfolioResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/portfolio`, {
      method: 'POST',
      headers: authHeaders(),
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || error.message || `Failed to upload portfolio: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error uploading portfolio:', error);
    throw error;
  }
};

// Update portfolio item
export const updatePortfolio = async (id: string, data: FormData | Partial<PortfolioItem>): Promise<SinglePortfolioResponse> => {
  try {
    const isFormData = data instanceof FormData;
    const response = await fetch(`${API_BASE_URL}/api/portfolio/${id}`, {
      method: 'PUT',
      headers: isFormData
        ? authHeaders()
        : { 'Content-Type': 'application/json', ...authHeaders() },
      body: isFormData ? data : JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || error.message || `Failed to update portfolio: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating portfolio:', error);
    throw error;
  }
};

// Delete portfolio item
export const deletePortfolio = async (id: string): Promise<void> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/portfolio/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || error.message || `Failed to delete portfolio: ${response.statusText}`);
    }
  } catch (error) {
    console.error('Error deleting portfolio:', error);
    throw error;
  }
};

// Bulk upload portfolio items
export const bulkUploadPortfolio = async (items: BulkUploadItem[]): Promise<BulkUploadResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/portfolio/bulk-upload`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders(),
      },
      body: JSON.stringify(items),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || error.message || `Failed to bulk upload: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error bulk uploading portfolio:', error);
    throw error;
  }
};

